<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { goto } from '$app/navigation';
  import {
    Building2,
    ListChecks,
    Loader2,
    Lock,
    Pencil,
    Trash2,
    UserRound,
    UserX,
    Users,
    X
  } from 'lucide-svelte';

  import ConfirmDialog from '$lib/components/ConfirmDialog.svelte';
  import SavedListDialog from '$lib/components/SavedListDialog.svelte';
  import { notifications } from '$lib/stores/notifications';
  import {
    discardSavedList,
    openList,
    openListLoading,
    removeSavedListRecord,
    renameSavedList
  } from '$lib/stores/savedlists';
  import { seatSelection } from '$lib/stores/seatselection';
  import { companiesFromRecords } from '$lib/utils/listselection';
  import { pageCountFor, pageWindow } from '$lib/utils/tablesort';
  import { skeleton } from '$lib/api/selection';
  import { showResults } from '$lib/stores/navigate';
  import type { SavedListRecord } from '$lib/api/savedlists';

  /**
   * One saved list, and what is in it.
   *
   * # Drawn from the snapshot, not from the corpus
   *
   * Every record carries the company name, the person's name and their role as
   * they were when it was added. That is what lets this screen render without
   * asking the corpus anything, and it is why somebody who has since left the
   * company still appears in the list they were put into.
   *
   * `in_corpus: false` means there is no live row behind a person any more. It
   * is not an error and must not read like one — the record is intact. What it
   * changes is one thing: there is nobody to open, so no profile link.
   */

  let renaming = $state(false);
  let confirmingDelete = $state(false);
  let removing = $state<string>('');

  /** Records with a person first: they are the ones that took work to collect. */
  const records = $derived($openList?.records ?? []);

  /**
   * A page of records at a time.
   *
   * Twenty, against the employees table's ten: a list row is one line, and a
   * customer scanning what they have collected is reading rather than
   * comparing. A list of 4,806 — which one here already is — was otherwise a
   * single scroll with no way to say where you are in it.
   *
   * The arithmetic is `$lib/utils/tablesort`, shared with that table and
   * tested: a page window off by one hides the last page, which is exactly the
   * kind of thing nobody sees on a list of three.
   */
  const perPage = 20;
  let page = $state(1);

  const pageCount = $derived(pageCountFor(records.length, perPage));

  /**
   * The page being shown.
   *
   * Clamped rather than trusted: removing the last record on the last page
   * would otherwise leave the view on a page that no longer exists, showing
   * nothing at all.
   */
  const current = $derived(Math.min(page, pageCount));
  const visible = $derived(records.slice((current - 1) * perPage, current * perPage));
  const pages = $derived(pageWindow(current, pageCount));

  function go(to: number) {
    page = Math.min(Math.max(1, to), pageCount);
  }

  // A different list is a different set of pages, and page four of the last
  // one means nothing in this one.
  $effect(() => {
    void $openList?.id;
    page = 1;
  });

  function openCompany(record: SavedListRecord) {
    void goto(`/company/${record.company_id}`);
  }

  async function remove(record: SavedListRecord) {
    removing = record.id;
    try {
      await removeSavedListRecord(record.id);
    } catch {
      notifications.error($_('savedLists.view.removeFailed'));
    } finally {
      removing = '';
    }
  }

  async function rename(name: string, scope: 'user' | 'organisation') {
    const list = $openList;
    if (!list) return;

    await renameSavedList(list.id, name, scope);
    renaming = false;
  }

  /**
   * Put this list on screen as a selection.
   *
   * # One company per employer, carrying the people chosen at it
   *
   * `companiesFromRecords` does the grouping, and says there why it is not a
   * filter on `kind`.
   *
   * # So it prices as what it holds
   *
   * A list of companies prices as companies; a list of people prices as those
   * people. `billableRecords` counts the contact identifiers when there are
   * any. Said out loud because a list of forty companies and one person is
   * neither of the two obvious numbers.
   *
   * # Somebody the corpus has lost is left behind
   *
   * `in_corpus: false` means no live row stands behind that person any more.
   * Their company still comes, because the record still names it; they do not,
   * because there is nothing to deliver. The count says so rather than letting
   * a quietly shorter export be discovered on opening the file.
   */
  function loadAsSelection() {
    const list = $openList;
    if (!list) return;

    const { companies, lost } = companiesFromRecords(list.records);

    if (companies.length === 0) {
      notifications.info($_('savedLists.view.loadNothing'));
      return;
    }

    seatSelection(
      companies.map((company) =>
        skeleton({
          id: company.id,
          name: company.name,
          contact_ids: company.contactIDs,
          // A list knows neither. The size arrives with the row when the block
          // is hydrated; the score is a search's measure of how well a company
          // answered a query, and there was no query — `seatSelection` widens
          // the score filter for exactly this reason, or a floor left over from
          // the last search would hide every row here.
          size: '',
          quality: 0
        })
      )
    );

    // One call, and it says everything arriving at the data view requires:
    // classic mode, no list open, no overlay. Doing it by hand here is how the
    // neighbour screen ended up drawn over a freshly seated selection.
    showResults();

    if (lost > 0) {
      notifications.info(
        $_('savedLists.view.loadedWithoutGone', { values: { count: companies.length, lost } })
      );
    } else {
      notifications.success($_('savedLists.view.loaded', { values: { count: companies.length } }));
    }
  }

  async function discard() {
    const list = $openList;
    if (!list) return;

    confirmingDelete = false;
    try {
      await discardSavedList(list.id);
      notifications.success($_('savedLists.view.deleted', { values: { name: list.name } }));
    } catch {
      notifications.error($_('savedLists.view.deleteFailed'));
    }
  }
</script>

<div class="relative flex h-full flex-col overflow-y-auto px-4 py-10 sm:px-6">
  {#if $openListLoading}
    <div class="flex flex-1 items-center justify-center gap-2 text-sm text-gray-500">
      <Loader2 class="h-4 w-4 animate-spin" />
      {$_('savedLists.view.loading')}
    </div>
  {:else if $openList}
    <!-- The dashboard's width. A list is read the same way its rows were
         collected, and two front screens at two widths read as two products. -->
    <div class="mx-auto w-full max-w-5xl">
      <div class="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div class="min-w-0">
          <h1 class="truncate text-xl font-semibold text-gray-900">{$openList.name}</h1>
          <p class="mt-1 flex items-center gap-2 text-sm text-gray-500">
            <span>
              {$_('savedLists.view.entries', { values: { count: $openList.entries } })}
            </span>
            <span aria-hidden="true">·</span>
            <span class="inline-flex items-center gap-1">
              {#if $openList.scope === 'organisation'}
                <Users class="h-3.5 w-3.5" />
                {$_('savedLists.scope.organisation')}
              {:else}
                <Lock class="h-3.5 w-3.5" />
                {$_('savedLists.scope.user')}
              {/if}
            </span>
            {#if $openList.scope === 'organisation' && !$openList.mine}
              <!--
                Whose it is, without naming them. `mine` is on the wire for
                exactly this: the app can tell its own from a colleague's, and
                the owner identifier is not published to the organisation.
              -->
              <span aria-hidden="true">·</span>
              <span>{$_('savedLists.view.colleagues')}</span>
            {/if}
          </p>
        </div>

        <div class="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onclick={loadAsSelection}
            disabled={records.length === 0}
            title={$_('savedLists.view.loadHint')}
            class="inline-flex items-center gap-1.5 rounded-control bg-accent-600 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-accent-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500"
          >
            <ListChecks class="h-4 w-4" />
            {$_('savedLists.view.load')}
          </button>
          <button
            type="button"
            onclick={() => (renaming = true)}
            class="inline-flex items-center gap-1.5 rounded-control border border-hairline px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            <Pencil class="h-4 w-4" />
            {$_('savedLists.view.rename')}
          </button>
          <button
            type="button"
            onclick={() => (confirmingDelete = true)}
            class="inline-flex items-center gap-1.5 rounded-control border border-hairline px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-red-50 hover:text-red-700"
          >
            <Trash2 class="h-4 w-4" />
            {$_('buttons.delete')}
          </button>
        </div>
      </div>

      {#if records.length === 0}
        <div
          class="rounded-surface border border-dashed border-hairline bg-white px-6 py-12 text-center"
        >
          <p class="text-sm font-medium text-gray-900">{$_('savedLists.view.emptyTitle')}</p>
          <p class="mt-1 text-sm text-gray-500">{$_('savedLists.view.emptyBody')}</p>
        </div>
      {:else}
        <div class="overflow-hidden rounded-surface border border-hairline bg-white">
          <ul class="divide-y divide-hairline">
            {#each visible as record (record.id)}
              <li
                class="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-surface-canvas"
              >
                <span
                  class="flex h-8 w-8 shrink-0 items-center justify-center rounded-surface {record.in_corpus
                    ? 'bg-surface-sunken text-gray-500'
                    : 'bg-amber-50 text-amber-600'}"
                >
                  {#if record.kind === 'contact'}
                    {#if record.in_corpus}
                      <UserRound class="h-4 w-4" />
                    {:else}
                      <UserX class="h-4 w-4" />
                    {/if}
                  {:else}
                    <Building2 class="h-4 w-4" />
                  {/if}
                </span>

                <div class="min-w-0 flex-1">
                  {#if record.kind === 'contact'}
                    <p class="truncate text-sm font-medium text-gray-900">
                      {record.name || $_('savedLists.view.unnamedPerson')}
                      {#if record.role}
                        <span class="font-normal text-gray-500"> · {record.role}</span>
                      {/if}
                    </p>
                    <p class="truncate text-xs text-gray-500">{record.company_name}</p>
                  {:else}
                    <p class="truncate text-sm font-medium text-gray-900">{record.company_name}</p>
                  {/if}
                </div>

                {#if !record.in_corpus}
                  <!--
                  Not an error. The record is whole — it is keyed on who this
                  person is and carries its own snapshot — but there is no live
                  row to open, so this says so instead of offering a link that
                  would land nowhere.
                -->
                  <span
                    class="hidden shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700 sm:inline"
                    title={$_('savedLists.view.goneHint')}
                  >
                    {$_('savedLists.view.gone')}
                  </span>
                {:else}
                  <button
                    type="button"
                    onclick={() => openCompany(record)}
                    class="hidden shrink-0 text-xs font-medium text-accent-600 hover:underline sm:inline"
                  >
                    {$_('savedLists.view.openCompany')}
                  </button>
                {/if}

                <button
                  type="button"
                  onclick={() => remove(record)}
                  disabled={removing === record.id}
                  title={$_('savedLists.view.remove')}
                  class="shrink-0 rounded-control p-1.5 text-gray-300 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                >
                  <span class="sr-only">{$_('savedLists.view.remove')}</span>
                  {#if removing === record.id}
                    <Loader2 class="h-4 w-4 animate-spin" />
                  {:else}
                    <X class="h-4 w-4" />
                  {/if}
                </button>
              </li>
            {/each}
          </ul>

          <!--
            Only when there is more than one page. A list of six does not need
            to be told it is page one of one.
          -->
          {#if pageCount > 1}
            <div
              class="flex flex-wrap items-center justify-between gap-3 border-t border-hairline px-4 py-2"
            >
              <p class="text-xs text-gray-500">
                {$_('pagination.showing', {
                  values: {
                    from: (current - 1) * perPage + 1,
                    to: Math.min(current * perPage, records.length),
                    total: records.length
                  }
                })}
              </p>

              <nav class="flex items-center gap-1" aria-label={$_('pagination.label')}>
                <button
                  type="button"
                  onclick={() => go(current - 1)}
                  disabled={current === 1}
                  class="rounded-control px-2 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent"
                >
                  {$_('pagination.previous')}
                </button>

                {#each pages as number (number)}
                  <button
                    type="button"
                    onclick={() => go(number)}
                    aria-current={number === current ? 'page' : undefined}
                    class="min-w-[1.75rem] rounded-control px-2 py-1 text-xs font-medium tabular-nums transition-colors {number ===
                    current
                      ? 'bg-accent-600 text-white'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}"
                  >
                    {number}
                  </button>
                {/each}

                <button
                  type="button"
                  onclick={() => go(current + 1)}
                  disabled={current === pageCount}
                  class="rounded-control px-2 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent"
                >
                  {$_('pagination.next')}
                </button>
              </nav>
            </div>
          {/if}
        </div>
      {/if}
    </div>
  {/if}
</div>

{#if renaming && $openList}
  <SavedListDialog
    isRename
    initialName={$openList.name}
    initialScope={$openList.scope}
    onSave={rename}
    onCancel={() => (renaming = false)}
  />
{/if}

{#if confirmingDelete && $openList}
  <ConfirmDialog
    title={$_('savedLists.view.deleteTitle')}
    message={$_('savedLists.view.deleteMessage', { values: { name: $openList.name } })}
    points={[$_('savedLists.view.deletePoint', { values: { count: $openList.entries } })]}
    note={$_('savedLists.view.deleteNote')}
    confirmLabel={$_('buttons.delete')}
    onConfirm={discard}
    onCancel={() => (confirmingDelete = false)}
  />
{/if}
